import { Topbar as LoopUITopbar, theme } from '@loop/ui'
import { FilterAltSharp, Help, KeyboardArrowDownRounded, PestControl, Phone, SearchOutlined } from '@mui/icons-material'
import EditNotificationsIcon from '@mui/icons-material/EditNotifications'
import { AppBar, Box, Button, ButtonGroup, Collapse, IconButton, Toolbar, Typography, useMediaQuery, useScrollTrigger } from '@mui/material'
import React, { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import HamburgerIcon from 'src/components/Icon/HamburgerIcon'
import NewFilterComponent from 'src/components/NewFilterComponent/NewFilterComponent'
import ActionWizardDrawer from 'src/components/Search/ActionWizard/ActionWizardDrawer'
import SearchDialog from 'src/components/Search/SearchDialog'
import { useAuth } from 'src/context/AuthContext'
import { useBillingContext } from 'src/context/BillingContext'
import { usePrivateLayoutDrawerContext } from 'src/context/PrivateLayoutDrawerContext'
import { useSearchContext } from 'src/context/SearchContext'
import { useSnackData } from 'src/context/SnackContext'
import { useTopbarContext } from 'src/context/TopbarContext'
import { BASE_DOCS_URL } from 'src/utils/config/config'
import { canAccessNotificationsRoute, canAccessOktaSettingsRoute, canAccessUserManagementRoute } from 'src/utils/functions/accessLevel'
import BillingBanner from './BillingBanner'
import { drawerCloseWidthPx, drawerWidthPx } from './components/PrivateLayoutDrawer'

export default function Topbar({
  drawerState,
  children,
  title,
  handleReport,
  disableReport = false
}: {
  title: JSX.Element | React.ReactNode
  drawerState: boolean
  children: JSX.Element | React.ReactNode
  handleReport: () => void
  disableReport?: boolean
}): JSX.Element {
  const navigate = useNavigate()
  const { actionWizard, setActionWizard } = useSearchContext()
  const sm = useMediaQuery(theme.breakpoints.down('sm'))
  const lg = useMediaQuery(theme.breakpoints.down('lg'))
  const { toggleDrawer } = usePrivateLayoutDrawerContext()
  const { logout, currentUser, orgConfig } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0
  })
  const { hideFilters, filterProps } = useTopbarContext()
  const { activeBillingConfig } = useBillingContext()

  const filterBarRef = React.useRef<HTMLElement>(null)

  const [openFilterDrawer, setOpenFilterDrawer] = React.useState(false)
  const [filterBarOffsetHeight, setFilterBarOffsetHeight] = React.useState<number>(0)
  const [billingBarOffsetHeight, setBillingBarOffsetHeight] = React.useState<number>(0)
  const [openSearchDialog, setOpenSearchDialog] = React.useState(false)

  const ctrlOrCmd = React.useMemo(() => {
    let isMac = false
    if (typeof navigator !== 'undefined') {
      isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0 || navigator.userAgent.toUpperCase().indexOf('MAC') >= 0
    }
    return isMac ? '⌘' : '⌘'
  }, [])

  React.useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      setFilterBarOffsetHeight(filterBarRef.current?.offsetHeight || 0)
    })

    if (filterBarRef.current) resizeObserver.observe(filterBarRef.current)

    return () => {
      resizeObserver.disconnect()
    }
  }, [filterBarRef?.current])

  // Add keyboard shortcut for search dialog
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for Cmd+K (Mac) or Ctrl+K (Windows/Linux)
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault()
        setOpenSearchDialog((prev) => !prev)
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  const menuOptions = React.useMemo(() => {
    const result: {
      label: string
      path: string
      icon: React.ReactNode | JSX.Element
      accessReqd: boolean
    }[] = []

    result.push({
      path: '/user/profile',
      label: 'User Profile',
      icon: 'account_circle',
      accessReqd: false
    })

    if (canAccessUserManagementRoute(orgConfig, currentUser)) {
      result.push({
        label: 'User Management',
        icon: 'people',
        path: '/user/management',
        accessReqd: false
      })
    }

    if (canAccessNotificationsRoute(orgConfig, currentUser)) {
      result.push({
        label: 'Notifications',
        icon: <EditNotificationsIcon />,
        path: '/notification-settings',
        accessReqd: false
      })
    }

    if (canAccessOktaSettingsRoute(orgConfig, currentUser)) {
      result.push({
        label: 'Okta Settings',
        icon: 'security',
        path: '/settings/okta',
        accessReqd: false
      })
    }

    return result
  }, [currentUser, orgConfig])

  const renderOptions = () => {
    const { openError } = useSnackData()
    const inputRef = React.useRef(null)

    // ===== Functions =====
    const focusOnInput = () => {
      inputRef.current?.focus()
    }

    const callFunction = () => (window.location.href = `tel:+17076414146`)
    const helpFunction = () => {
      // Open docs in new tab - session cookie handles authentication automatically
      window.open(BASE_DOCS_URL, '_blank', 'noopener,noreferrer')
    }

    useEffect(() => {
      if (searchParams?.get('report-issue') && searchParams?.get('report-issue') === 'true') {
        handleReport()
        setSearchParams((prev) => {
          prev.delete('report-issue')
          return prev
        })
      }
    }, [searchParams?.get('report-issue')])

    useEffect(() => {
      if (searchParams?.get('call-support') && searchParams?.get('call-support') === 'true') {
        callFunction()
        setSearchParams((prev) => {
          prev.delete('call-support')
          return prev
        })
      }
    }, [searchParams?.get('call-support')])

    return (
      <>
        <SearchDialog
          open={openSearchDialog}
          handleClose={() => setOpenSearchDialog(false)}
        />
        <ActionWizardDrawer
          open={!!actionWizard}
          handleClose={() => setActionWizard(null)}
          actionWizard={actionWizard}
        />

        {sm ? (
          <Box width="100%">
            <IconButton
              sx={{ width: 45, height: 45, color: 'black' }}
              onClick={toggleDrawer}>
              <HamburgerIcon
                sx={{
                  color: '#D9D9D9'
                }}
              />
            </IconButton>
          </Box>
        ) : (
          <>
            {lg ? (
              <Box display="flex">
                <IconButton
                  size="large"
                  aria-label="open searchbar"
                  onClick={() => {
                    setOpenSearchDialog(true)
                    focusOnInput()
                  }}
                  sx={{
                    width: 45,
                    height: 45,
                    color: 'black'
                  }}>
                  <SearchOutlined />
                </IconButton>
                <IconButton
                  sx={{ width: 45, height: 45, color: 'black' }}
                  onClick={callFunction}>
                  <Phone />
                </IconButton>
                <IconButton
                  sx={{ width: 45, height: 45, color: 'black' }}
                  onClick={helpFunction}>
                  <Help />
                </IconButton>
                <IconButton
                  disabled={disableReport}
                  sx={{ width: 45, height: 45, color: 'black' }}
                  onClick={handleReport}>
                  <PestControl />
                </IconButton>
              </Box>
            ) : (
              <>
                <Button
                  sx={{
                    border: '1px solid #E6E6E6',
                    borderRadius: '4px',
                    color: 'black',
                    fontSize: 12,
                    fontWeight: 400,
                    mr: 2,
                    height: '35px',
                    bgcolor: '#fff',
                    px: 0.5,
                    py: 0,
                    '&:hover': {
                      bgcolor: '#fff',
                      borderColor: '#196E82'
                    }
                  }}
                  disableElevation
                  startIcon={<SearchOutlined sx={{ ml: 1 }} />}
                  onClick={() => {
                    setOpenSearchDialog(true)
                    focusOnInput()
                  }}>
                  Search...
                  <Typography
                    component="span"
                    lineHeight="initial"
                    fontSize={12}
                    color="#000"
                    px={1}
                    py={0.5}
                    border="1px solid #E6E6E6"
                    borderRadius="4px"
                    ml={1.5}
                    bgcolor="#F6F6F6"
                    whiteSpace="nowrap">
                    {ctrlOrCmd} + K
                  </Typography>
                </Button>
                <ButtonGroup
                  variant="outlined"
                  sx={{
                    mr: 2,
                    height: 35
                  }}>
                  <Button
                    sx={{
                      border: '1px solid #E6E6E6',
                      borderRadius: '4px',
                      color: 'black',
                      fontSize: 12,
                      fontWeight: 400
                    }}
                    startIcon={<Phone />}
                    onClick={callFunction}>
                    {'+1 7076414146'}
                  </Button>
                  <Button
                    sx={{
                      border: '1px solid #E6E6E6',
                      borderRadius: '4px',
                      color: 'black',
                      fontSize: 12,
                      fontWeight: 400
                    }}
                    startIcon={<Help />}
                    onClick={helpFunction}>
                    {'Help'}
                  </Button>
                  <Button
                    disableRipple={true}
                    sx={{
                      border: '1px solid #E6E6E6',
                      borderRadius: '4px',
                      color: 'black',
                      fontSize: 12,
                      fontWeight: 400,
                      '&:disabled': {
                        color: 'black',
                        opacity: 0.5,
                        background: 'white'
                      }
                    }}
                    disabled={disableReport}
                    startIcon={<PestControl />}
                    onClick={handleReport}>
                    {'Report an Issue'}
                  </Button>
                </ButtonGroup>
              </>
            )}
          </>
        )}
      </>
    )
  }

  return (
    <LoopUITopbar
      title={sm ? null : title}
      currentUser={currentUser}
      drawerState={drawerState}
      navigate={navigate}
      logout={logout}
      menuOptions={menuOptions}
      renderOptions={renderOptions}
      hideUserInfo={true}>
      <BillingBanner
        drawerState={drawerState}
        billingBarOffsetHeight={billingBarOffsetHeight}
        setBillingBarOffsetHeight={setBillingBarOffsetHeight}
      />

      {!hideFilters && (
        <>
          <AppBar
            ref={filterBarRef}
            position="fixed"
            elevation={trigger ? 3 : 0}
            sx={{
              backgroundColor: 'white',
              bgcolor: 'white',
              borderBottom: '1px solid #E6E6E6',
              top: activeBillingConfig ? `calc(67px + ${billingBarOffsetHeight}px)` : '67px',
              width: {
                sm: drawerState ? `calc(100vw - ${drawerWidthPx})` : `calc(100vw - ${drawerCloseWidthPx})`,
                xs: '100%'
              },
              left: {
                sm: drawerState ? drawerWidthPx : drawerCloseWidthPx,
                xs: 'auto'
              },
              transition: theme.transitions.create(['width', 'left'], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen
              })
            }}>
            {sm ? (
              <>
                <Button
                  variant="text"
                  sx={{
                    height: '50px',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderRadius: 0,
                    color: '#000',
                    px: 2.5,
                    bgcolor: openFilterDrawer ? '#F3F3F3' : '#FFFFFF',
                    transition: '0.3s ease'
                  }}
                  onClick={() => setOpenFilterDrawer(!openFilterDrawer)}>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1}>
                    <FilterAltSharp />
                    <Typography
                      color="inherit"
                      fontSize={14}>
                      Filters
                    </Typography>
                  </Box>
                  <Box
                    display="flex"
                    alignItems="center">
                    <KeyboardArrowDownRounded sx={{ fontSize: '24px', transform: openFilterDrawer ? 'rotate(180deg)' : '', transition: '0.3s ease' }} />
                  </Box>
                </Button>

                <Collapse in={openFilterDrawer}>
                  <Box
                    sx={{
                      left: 0,
                      width: '100%',
                      height: '100%',
                      minHeight: 'calc(100vh - 120px)',
                      overflow: 'auto',
                      bgcolor: '#fff',
                      zIndex: 1000,
                      py: 1,
                      px: 2
                    }}>
                    <NewFilterComponent
                      showFiltersOutsideDropdown
                      fullWidthFilters
                      closeFilterDrawer={() => setOpenFilterDrawer(false)}
                      {...filterProps}
                    />
                  </Box>
                </Collapse>
              </>
            ) : (
              <Toolbar>
                <NewFilterComponent
                  showFiltersOutsideDropdown
                  {...filterProps}
                />
              </Toolbar>
            )}
          </AppBar>
          <Toolbar sx={{ height: filterBarOffsetHeight }} />
        </>
      )}
      {children}
    </LoopUITopbar>
  )
}
